const URL_PREFIX_SOURCE = /[a-zA-Z0-9][a-zA-Z0-9.-]*\./.source;
const URL_SUFFIX_SOURCE = /(?::[0-9]+)?([/?#].*)?/.source;
const TRAILING_PUNCTUATION = /([.,:?!)\]]{1,3})$/;
const HTTP_SCHEME_REGEX = /^https?:\/\//i;
const UNSAFE_URL_CHARACTER_REGEX = /[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u202a-\u202e\u2066-\u2069]/;
// ASCII-only authorities avoid raw IDN homograph links; punycode stays ASCII.
const SAFE_AUTHORITY_REGEX =
  /^[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)+(?::[0-9]{1,5})?$/;
const TLD_REGEX = new RegExp(`^${URL_PREFIX_SOURCE}[a-z]{2,}${URL_SUFFIX_SOURCE}$`, "i");

function hasSafeAuthority(word: string): boolean {
  const authority = word.replace(HTTP_SCHEME_REGEX, "").split(/[/?#]/, 1)[0];
  return SAFE_AUTHORITY_REGEX.test(authority);
}

function isLink(word: string): boolean {
  if (!word) return false;
  if (UNSAFE_URL_CHARACTER_REGEX.test(word)) return false;
  if (/^https?:\/\/[a-zA-Z0-9]/i.test(word)) return hasSafeAuthority(word);
  if (/^www\.[a-zA-Z0-9]/i.test(word)) return hasSafeAuthority(word);
  return TLD_REGEX.test(word) && hasSafeAuthority(word);
}

function ensureHrefScheme(word: string): string {
  return HTTP_SCHEME_REGEX.test(word) ? word : `https://${word}`;
}

function extractTrailingPunctuation(word: string): { link: string; punctuation: string } {
  const match = TRAILING_PUNCTUATION.exec(word);
  return {
    link: word.replace(TRAILING_PUNCTUATION, ""),
    punctuation: match ? match[0] : "",
  };
}

export type LinkifyToken = { type: "text"; text: string } | { type: "link"; href: string; display: string };

/**
 * Split text into plain-text and URL tokens while preserving whitespace.
 * Only http(s), www, and ASCII bare domains with alphabetic TLDs become links; scheme-based
 * URIs and userinfo-style spoofing with "@" stay inert text.
 */
export function linkify(text: string): LinkifyToken[] {
  if (!text) return [];

  const tokens: LinkifyToken[] = [];
  let buffer = "";
  const parts = text.split(/(\s+)/);

  for (const part of parts) {
    const { link, punctuation } = extractTrailingPunctuation(part);
    if (isLink(link)) {
      if (buffer) {
        tokens.push({ type: "text", text: buffer });
        buffer = "";
      }
      tokens.push({ type: "link", href: ensureHrefScheme(link), display: link });
      if (punctuation) buffer += punctuation;
    } else {
      buffer += part;
    }
  }
  if (buffer) tokens.push({ type: "text", text: buffer });
  return tokens;
}
